/**
 * Declare an abstract requirement TYPE.
 *
 * A type is the thing a requirement is typed BY: a level combined with whatever
 * the statement is about, carrying the attributes that statement is made of and,
 * as its template, the sentence its usages render.
 *
 * Until this existed a program's types could only be hand-written into the
 * .sysml and re-imported, which is fine for the shared library and wrong for a
 * program - BEST is the thing people have open.
 *
 * There is no package picker, for the same reason the requirement dialog has
 * none: where a program's types live is already decided - a `RequirementTypes`
 * package at the top of its Requirements - so asking would be asking a question
 * whose answer is fixed. The server creates it the first time.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import NewAttributesTable from '../parts/NewAttributesTable'
import { ApiError, api } from '../../api'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import {
  attributePayload,
  emptyAttributeDraft,
} from '../parts/part-wizard-model'
import type { AttributeDraft } from '../parts/part-wizard-model'
import type { AttributeTypeChoice, KindSchema } from '../../schema'

/** '{allocated_to}' - one feature slot. Matches metamodel/statements.py. */
const SLOT = /\{([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\}/g

/** The feature names a template fills in, in the order they read. */
export function slotsIn(template: string): string[] {
  const found: string[] = []
  for (const match of template.matchAll(SLOT)) {
    if (!found.includes(match[1])) found.push(match[1])
  }
  return found
}

/**
 * A slot the finished type will not be able to fill.
 *
 * The renderer leaves an unfillable slot AS the slot, so the statement comes out
 * reading "The {allocated_to} shall …". That is honest and it is still wrong,
 * and it is far cheaper to say so here than to find it on the first requirement
 * somebody writes.
 *
 * `carries` is what the chosen bases already provide - the server knows, because
 * it resolved those types - plus whatever this form is about to declare.
 */
export function unfillable(template: string, carries: readonly string[]): string[] {
  return slotsIn(template).filter((slot) => !carries.includes(slot))
}

export default function CreateRequirementTypeDialog({
  kind,
  onClose,
  onCreated,
}: {
  kind: KindSchema
  onClose: () => void
  onCreated: (name: string) => void
}) {
  const toasts = useToasts()

  const [name, setName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [levelType, setLevelType] = useState('L3Requirement')
  const [combines, setCombines] = useState<string[]>([])
  const [template, setTemplate] = useState('')
  const [attributes, setAttributes] = useState<AttributeDraft[]>([
    emptyAttributeDraft('attr-0'),
  ])

  // The ISQ kinds an attribute may be declared as, from the server - the same
  // list the part wizard offers, because it is the same question.
  const [attributeTypes, setAttributeTypes] = useState<AttributeTypeChoice[]>([])
  useEffect(() => {
    let live = true
    api.attributeTypes()
      .then((answer) => live && setAttributeTypes(answer))
      .catch(() => live && setAttributeTypes([]))
    return () => {
      live = false
    }
  }, [])

  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  // The LEVELS are the types that carry one and add no fields of their own -
  // L0Requirement through L4Requirement. Read off the schema rather than
  // listed, so a sixth level would appear here without an edit.
  const levels = kind.types.filter((entry) => entry.level !== null && !entry.fields.length)

  // What a new type may COMBINE with a level. A DIFFERENT list from the one
  // above, and that is the point: `types` is what a requirement can BE, so it
  // holds only what reaches a level - and MassRequirement, the thing anyone
  // actually wants to combine with, reaches none by design.
  const generics = kind.base_types

  // Everything the finished type will carry: the slots its bases already fill,
  // plus the attributes about to be declared here. `allocated_to` is the common
  // case - it comes from AllocatedRequirement, which the level types below L2
  // already specialize.
  const inherited = [...kind.types, ...kind.base_types]
    .filter((entry) => combines.includes(entry.name) || entry.name === levelType)
    .flatMap((entry) => slotsIn(entry.template))
  const carries = [
    ...new Set([
      ...inherited,
      // Every reference and scoping feature a requirement always has: these are
      // fillable in a template without being declared here.
      'allocated_to', 'allocated_function', 'addresses', 'segment', 'subsystem',
      ...attributes.map((entry) => entry.name.trim()).filter(Boolean),
    ]),
  ]
  const holes = unfillable(template, carries)

  const submit = async () => {
    setBusy(true)
    setFailure('')
    try {
      const made = await api.createRequirementType({
        name,
        identifier: identifier || null,
        level_type: levelType,
        specializes: combines,
        template,
        // The wizard's own converter, so a quantity and a choice are spelled
        // the one way BEST already understands.
        attributes: attributePayload(attributes),
      })
      toasts.succeeded(`${made.name} declared`)
      onCreated(made.qualified_name)
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  const ready = Boolean(name.trim() && levelType && !busy)

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>New requirement type</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          <TextField
            label="Name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            size="small"
            helperText="How it reads in the model, e.g. SubsystemMassRequirement"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />

          <FormControl size="small" required>
            <InputLabel id="type-level">Level</InputLabel>
            <Select
              labelId="type-level"
              label="Level"
              value={levelType}
              onChange={(event) => setLevelType(event.target.value)}
            >
              {levels.map((choice) => (
                <MenuItem key={choice.name} value={choice.name}>
                  {choice.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* A level is MANDATORY and is asked separately above: a type that
              reaches none would give every requirement typed by it no level at
              all, and the server refuses one. This is what it is ABOUT. */}
          <FormControl size="small">
            <InputLabel id="type-combines">Combines with</InputLabel>
            <Select
              labelId="type-combines"
              label="Combines with"
              multiple
              value={combines}
              onChange={(event) =>
                setCombines(
                  typeof event.target.value === 'string'
                    ? event.target.value.split(',')
                    : event.target.value,
                )
              }
              renderValue={(chosen) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {chosen.map((entry) => (
                    <Chip key={entry} size="small" label={entry.split('::').pop()} />
                  ))}
                </Box>
              )}
            >
              {generics.map((choice) => (
                <MenuItem key={choice.name} value={choice.name}>
                  {choice.label || choice.name.split('::').pop()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          {/* The attributes the statement is made OF, declared before the
              template that names them - which is also the order the server
              writes them in. */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Attributes
            </Typography>
            <NewAttributesTable
              rows={attributes}
              choices={attributeTypes}
              onChange={setAttributes}
              disabled={busy}
              // Nothing rolls up a requirement: it is not a subtree whose
              // masses add. The column would reach a server that ignores it.
              showRollsUp={false}
              // What its requirements link to - an interface, a state, a port
              // (issue #48): the type's own connection types.
              showLinks
            />
          </Box>

          <TextField
            label="Statement template"
            value={template}
            onChange={(event) => setTemplate(event.target.value)}
            multiline
            minRows={2}
            size="small"
            error={holes.length > 0}
            helperText={
              holes.length
                ? `Nothing fills ${holes.join(', ')} — declare it above, or correct the name`
                : "The sentence its requirements render. Name a feature in braces, e.g. 'The {allocated_to} shall have a mass {expression} {mass}.'"
            }
          />

          <TextField
            label="ID"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            size="small"
            helperText="Optional; the <CODE> short name, if it has one"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!ready}>
          {busy ? 'Declaring…' : 'Declare'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
